import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

const LONG_PRESS_MS = 350;

/**
 * Tooltip for icon-only controls. Desktop: hover/focus. Touch: press and hold
 * (≥350 ms) shows it, release hides it. The label is also set as aria-label on
 * the child when it doesn't have one, so screen readers get it too.
 */
const Tip = ({
  label,
  children,
  className = "",
  side = "top",
}: {
  label: string;
  children: ReactNode;
  className?: string;
  /** "bottom" for controls at the top edge of the screen (header). */
  side?: "top" | "bottom";
}) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);

  const child = Children.only(children);
  const content =
    isValidElement(child) && !(child as ReactElement<any>).props["aria-label"]
      ? cloneElement(child as ReactElement<any>, { "aria-label": label })
      : child;

  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={() => setOpen(false)}
      onTouchStart={() => {
        clear();
        timer.current = setTimeout(() => setOpen(true), LONG_PRESS_MS);
      }}
      onTouchEnd={() => {
        clear();
        setOpen(false);
      }}
      onTouchCancel={() => {
        clear();
        setOpen(false);
      }}
    >
      {content}
      {open && (
        <span
          role="tooltip"
          className={`pointer-events-none absolute left-1/2 z-[1300] -translate-x-1/2 whitespace-nowrap rounded bg-[#0D1216] px-2 py-1 text-[11px] leading-none text-white shadow-lg ring-1 ring-white/10 ${
            side === "bottom" ? "top-full mt-1.5" : "bottom-full mb-1.5"
          }`}
        >
          {label}
        </span>
      )}
    </span>
  );
};

export default Tip;
